import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box, Typography } from "@mui/material";
import { shape } from "@app/design-tokens";
import { Link } from "react-router";

import { useAuth } from "@/auth/authContext";
import { serviceIcon } from "@/layout/serviceIcons";
import { PageHeader } from "@/layout/PageHeader";

/**
 * 「服務」這一節的落地頁：使用者能進入哪些服務。
 *
 * 完全由 `/me/` 的回傳渲染，前端不維護服務清單 —— 後端多開一個服務、或把
 * 某人的角色收回去，這一頁與側欄會同時跟著變，不需要改前端。
 *
 * 桌機上這一頁與左側第二層選單資訊重疊是刻意的：手機沒有第二層，這一頁
 * 就是進入服務的唯一入口，所以它必須自己站得住。
 */
export function ServicesPage() {
  const { me } = useAuth();
  const services = me?.services ?? [];
  const identity = [me?.display_name, me?.department].filter(Boolean).join(" · ");

  return (
    <>
      <PageHeader
        crumbs={[{ label: "服務" }]}
        title="服務"
        description={
          <>
            {identity}
            {identity && " · "}您目前可以使用{" "}
            <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
              {services.length}
            </Box>{" "}
            個服務
          </>
        }
      />

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto", px: 3, pb: 3 }}>
        {services.length === 0 ? (
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: `${shape.large}px`,
              p: 3,
            }}
          >
            <Typography variant="body1">您尚未被授予任何服務權限，請聯絡系統管理員。</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {services.map((service) => {
              const Icon = serviceIcon(service.code);
              return (
                <Box
                  key={service.code}
                  component={Link}
                  to={`/${service.code}`}
                  sx={{
                    minHeight: 190,
                    p: 2.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: `${shape.large}px`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.75,
                    color: "text.primary",
                    transition: (t) => t.transitions.create(["border-color", "background-color"]),
                    "&:hover": { borderColor: "text.primary", bgcolor: "action.hover" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: `${shape.medium}px`,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: `${shape.small}px`,
                        bgcolor: "action.hover",
                        color: "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ArrowForwardOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="h6" component="h2">
                      {service.name}
                    </Typography>
                    {service.description && (
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ flexGrow: 1 }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </>
  );
}
